import { useState } from "react";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_URL = "https://readdy.ai/api/form/db69ibfqhq9pk5mecrn0";

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta(
    "关于我们 - 国语对白做受69",
    "了解国语对白做受69网站的定位、内容体系与使用说明，并通过留言反馈渠道与我们联系。",
    "关于我们,国语对白做受69,国语对白,影音资源,网站介绍,意见反馈",
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }

    formData.delete("contact_alt");
    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (!response.ok || parsed?.code !== "OK" || /spam/i.test(serverMsg)) {
        setStatus("error");
        setFormError(serverMsg || "提交未成功，请稍后重试或检查填写内容。");
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors";

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 w-full">
        <section className="w-full bg-primary-950">
          <div className="mx-auto max-w-page px-4 md:px-8 py-10 md:py-14">
            <div className="text-background-300">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            </div>
            <h1 className="mt-4 text-2xl md:text-3xl font-bold text-background-50 font-heading">
              关于国语对白做受69
            </h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-background-200 leading-relaxed">
              我们专注于整理与呈现优质国语对白影音资源，用清晰的分类与多维筛选，让每一次查找都更轻松。
            </p>
          </div>
        </section>

        <div className="mx-auto max-w-page px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2 space-y-8">
              <section>
                <h2 className="text-lg font-bold text-foreground-950 font-heading flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500" />
                  网站定位
                </h2>
                <div className="mt-4 space-y-3 text-sm md:text-[15px] text-foreground-700 leading-loose">
                  <p>
                    国语对白做受69 是面向中文用户的综合内容型影音资源站，围绕「国语对白相关影音资源」这一核心主题，
                    汇集电影、电视剧、纪录片、综艺、动漫与微电影等多元内容。
                  </p>
                  <p>
                    我们通过专业的分类体系与地区、年份、类型多维筛选，以及标签导航和关联推荐，
                    帮助用户快速查询、浏览与了解自己感兴趣的国语影音作品。
                  </p>
                  <p>
                    本站为内容展示与介绍平台，所有内容仅用于介绍与浏览，不提供在线播放、下载、会员及支付服务。
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-lg font-bold text-foreground-950 font-heading flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500" />
                  使用说明
                </h2>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: "ri-search-line", title: "站内搜索", desc: "在顶部或搜索页输入关键词，支持标题、简介、主创与标签检索。" },
                    { icon: "ri-filter-3-line", title: "多维筛选", desc: "分类页与搜索页支持地区、年份、类型多选筛选，并可一键清空。" },
                    { icon: "ri-sort-desc", title: "排序浏览", desc: "可按最新更新或热门点击量排序，快速找到当季热门资源。" },
                    { icon: "ri-price-tag-3-line", title: "标签导航", desc: "点击作品标签可跳转搜索或相关分类，顺藤摸瓜发现更多内容。" },
                  ].map((tip) => (
                    <div key={tip.title} className="rounded-lg border border-background-200 bg-background-100 p-4">
                      <div className="flex items-center gap-2.5">
                        <span className="w-9 h-9 rounded-lg bg-background-50 flex items-center justify-center shrink-0">
                          <i className={`${tip.icon} text-primary-600 text-lg`} />
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-900">{tip.title}</h3>
                      </div>
                      <p className="mt-2.5 text-xs md:text-[13px] text-foreground-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-lg font-bold text-foreground-950 font-heading flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500" />
                  联系方式与反馈渠道
                </h2>
                <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 space-y-3">
                  <p className="flex items-center gap-2.5 text-sm text-foreground-700">
                    <i className="ri-mail-line text-accent-500 text-base" />
                    意见反馈邮箱：feedback@guoyu69.example
                  </p>
                  <p className="flex items-center gap-2.5 text-sm text-foreground-700">
                    <i className="ri-time-line text-accent-500 text-base" />
                    工作日 09:00 - 18:00 会有专人查看留言
                  </p>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    你也可以使用右侧留言表单提交内容建议、资源补充或使用问题，我们会认真阅读每一条反馈。
                  </p>
                </div>
              </section>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-24">
                <h2 className="text-base font-semibold text-foreground-900 font-heading flex items-center gap-2">
                  <i className="ri-chat-3-line text-accent-500" />
                  给我们留言
                </h2>
                <p className="mt-2 text-xs text-foreground-500">
                  填写下方表单，我们会尽快查看你的反馈。
                </p>

                {status === "success" ? (
                  <div className="mt-5 rounded-md border border-accent-200 bg-accent-50 p-4 text-center">
                    <i className="ri-checkbox-circle-line text-2xl text-accent-600" />
                    <p className="mt-2 text-sm font-medium text-foreground-800">留言已提交，感谢你的反馈！</p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-3 text-xs text-primary-600 hover:text-primary-700 cursor-pointer"
                    >
                      再写一条
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="about-feedback-form"
                    onSubmit={handleSubmit}
                    className="mt-4 space-y-3.5"
                  >
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-extra-check"
                    />
                    <div>
                      <label htmlFor="name" className="mb-1.5 block text-xs font-medium text-foreground-600">
                        称呼
                      </label>
                      <input id="name" name="name" type="text" required placeholder="请输入你的称呼" className={inputClass} />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-foreground-600">
                        邮箱
                      </label>
                      <input id="email" name="email" type="email" required placeholder="name@example.com" className={inputClass} />
                    </div>
                    <div>
                      <label htmlFor="subject" className="mb-1.5 block text-xs font-medium text-foreground-600">
                        反馈类型
                      </label>
                      <select id="subject" name="subject" required className={inputClass} defaultValue="">
                        <option value="" disabled>
                          请选择反馈类型
                        </option>
                        <option value="内容建议">内容建议</option>
                        <option value="资源补充">资源补充</option>
                        <option value="使用问题">使用问题</option>
                        <option value="合作咨询">合作咨询</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="mb-1.5 block text-xs font-medium text-foreground-600">
                        留言内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        placeholder="请输入你的留言（最多 500 字）"
                        className={`${inputClass} resize-none`}
                      />
                    </div>

                    {status === "error" && (
                      <p className="rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">{formError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="w-full rounded-md bg-primary-600 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "submitting" ? "提交中…" : "提交留言"}
                    </button>
                  </form>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}